/**
 * Two-level catalog picker: manufacturer → model.
 */

import { useCallback, useMemo, useState } from 'react';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { ICatalogDescriptor } from '@/workflows/vascular/common/catalog';
import { EVAR_CATALOG_DESCRIPTORS } from '@/workflows/vascular/evar/strategy';

interface CatalogSelectorProps {
    selectedId: string | null;
    onSelect: (id: string | null) => void;
}

export function CatalogSelector({ selectedId, onSelect }: CatalogSelectorProps) {
    // Derive the current manufacturer from the selected catalog
    const selectedDescriptor = useMemo(
        () => EVAR_CATALOG_DESCRIPTORS.find((d) => d.id === selectedId) ?? null,
        [selectedId]
    );

    const [manufacturer, setManufacturer] = useState<string | null>(selectedDescriptor?.manufacturer ?? null);

    // Group catalogs by manufacturer
    const grouped = useMemo(() => {
        const groups = new Map<string, ICatalogDescriptor[]>();
        for (const desc of EVAR_CATALOG_DESCRIPTORS) {
            const list = groups.get(desc.manufacturer) ?? [];
            list.push(desc);
            groups.set(desc.manufacturer, list);
        }
        return groups;
    }, []);

    const manufacturers = useMemo(() => [...grouped.keys()].sort(), [grouped]);

    /** Look up the logo for a manufacturer (uses first descriptor that has one). */
    const getManufacturerLogo = useCallback(
        (mfr: string) => (grouped.get(mfr) ?? []).find((d) => d.logo)?.logo,
        [grouped]
    );

    const models = useMemo(
        () => (manufacturer ? (grouped.get(manufacturer) ?? []) : []),
        [manufacturer, grouped]
    );

    const handleManufacturerChange = useCallback(
        (value: string) => {
            setManufacturer(value);
            // If only one model for this manufacturer, auto-select it
            const mods = grouped.get(value) ?? [];
            if (mods.length === 1) {
                onSelect(mods[0].id);
            } else {
                // Clear model if switching manufacturer
                if (selectedDescriptor?.manufacturer !== value) {
                    onSelect(null);
                }
            }
        },
        [grouped, onSelect, selectedDescriptor]
    );

    const handleModelChange = useCallback(
        (value: string) => {
            onSelect(value || null);
        },
        [onSelect]
    );

    return (
        <div className="px-3 py-2 border-b border-border flex gap-2">
            {/* Manufacturer never truncates: sized to its content, the Model select shrinks first */}
            <div className="shrink-0">
                <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">
                    Manufacturer
                </label>
                <Select value={manufacturer ?? ''} onValueChange={handleManufacturerChange}>
                    <SelectTrigger className="w-full text-xs h-8 [&>span]:line-clamp-none [&>span]:whitespace-nowrap">
                        <SelectValue placeholder="Select manufacturer..." />
                    </SelectTrigger>
                    <SelectContent className="w-max min-w-[var(--radix-select-trigger-width)]">
                        {manufacturers.map((mfr) => {
                            const logo = getManufacturerLogo(mfr);
                            return (
                                <SelectItem key={mfr} value={mfr} className="text-xs">
                                    <span className="flex items-center gap-2 whitespace-nowrap">
                                        {logo && <img src={logo} alt="" className="h-4 w-6 object-contain" />}
                                        {mfr}
                                    </span>
                                </SelectItem>
                            );
                        })}
                    </SelectContent>
                </Select>
            </div>
            <div className="flex-1 min-w-0">
                <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">
                    Model
                </label>
                <Select value={selectedId ?? ''} onValueChange={handleModelChange} disabled={!manufacturer}>
                    <SelectTrigger className="w-full text-xs h-8">
                        <SelectValue placeholder={manufacturer ? 'Select model...' : '—'} />
                    </SelectTrigger>
                    <SelectContent className="w-max min-w-[var(--radix-select-trigger-width)]">
                        {models.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id} className="text-xs whitespace-nowrap">
                                {cat.model}
                                <span className="ml-1 text-muted-foreground text-[10px]">
                                    ({cat.category.toUpperCase()})
                                </span>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        </div>
    );
}
